//#region User Paths Factory

/**
 * Factory for generating Firestore user paths in a user-centric and chainable way.
 *
 * This utility helps construct canonical Firestore paths under the `/users/{userId}/...`
 * hierarchy. It enforces setting a `userId` before generating any user-specific path.
 *
 * Each method returns a fully qualified Firestore document or collection path string,
 * ensuring type safety, consistency, and maintainability.
 *
 * ---
 * ### Core Pattern
 * 1. Call `createUserPaths()` to instantiate the factory.
 * 2. Chain `.setUserId(userId)` to initialize the current user context.
 * 3. Use any of the provided helpers (e.g. `.settings()`, `.user()`, `.customPath()`).
 * ---
 *
 * @example
 * const userPaths = createUserPaths().setUserId('user123');
 * const userDoc = userPaths.user();                    // → "/users/user123"
 * const lastSeen = userPaths.lastSeenFormPost();       // → "/users/user123/singular_documents/last_seen_form_post"
 * const settingsRoot = userPaths.settings();           // → "/users/user123/settings"
 * const tableNav = userPaths.tableNavigation();        // → "/users/user123/settings/table_navigation"
 * const mailer = userPaths.mailerSettings('storeABC'); // → "/users/user123/settings/stores/storeABC/mailer_settings"
 * const order = userPaths.orderSettings('storeABC');   // → "/users/user123/settings/stores/storeABC/order_settings"
 * const custom = userPaths.customPath('preferences', 'ui', 'theme');
 * // → "/users/user123/preferences/ui/theme"
 */
export const createUserPaths = (): {
  base: string;
  customPath(...segments: string[]): string;
  dateFormatting(): string;
  fitToGrid(): string;
  lastSeenFormPost(): string;
  mailerSettings(storeId: string): string;
  ngpEditsPreview(): string;
  orderSettings(storeId: string): string;
  savedPreferences(): string;
  setUserId(user: string): ReturnType<typeof createUserPaths>;
  settings(): string;
  settingsDoc(docName: string): string;
  settingsStore(storeId: string): string;
  settingsStores(): string;
  singularDoc(docName: string): string;
  singularDocs(): string;
  stockEditsPreview(): string;
  storeDisplayOrder(): string;
  tableNavigation(): string;
  user(): string;
} => {
  let userId: string | null = null;

  const ensureUserId = (): string => {
    if (!userId) throw new Error('User ID not set');
    return userId;
  };

  return {
    /** Base path for all user data */
    base: '/users',

    /**
     * Build the root user document path.
     *
     * @example
     * createUserPaths().setUserId('user123').user();
     * // → "/users/user123"
     */
    user(): string {
      return `${this.base}/${ensureUserId()}`;
    },

    /**
     * Build any custom path under the current user.
     *
     * @example
     * createUserPaths().setUserId('user123').customPath('inventory', 'active');
     * // → "/users/user123/inventory/active"
     */
    customPath(...segments: string[]): string {
      return `${this.base}/${ensureUserId()}/${segments.join('/')}`;
    },

    /** Shortcut for the `last_seen_form_post` singular document. */
    lastSeenFormPost(): string {
      return this.singularDoc('last_seen_form_post');
    },

    /** Path for the user's saved preferences document. */
    savedPreferences(): string {
      return this.settingsDoc('saved_preferences');
    },

    /**
     * Path for the user's settings root.
     * @example
     * createUserPaths().setUserId('user123').settings();
     * // → "/users/user123/settings"
     */
    settings(): string {
      return `${this.base}/${ensureUserId()}/settings`;
    },

    /** Path for a specific settings document under `/settings`. */
    settingsDoc(docName: string): string {
      return `${this.settings()}/${docName}`;
    },

    /** Path for a specific store's settings node. */
    settingsStore(storeId: string): string {
      return `${this.settingsStores()}/${storeId}`;
    },

    /** Path for the user's collection of per-store settings. */
    settingsStores(): string {
      return `${this.settings()}/stores`;
    },

    /** Path for a user's per-store mailer settings document. */
    mailerSettings(storeId: string): string {
      return `${this.settingsStore(storeId)}/mailer_settings`;
    },

    /** Shortcut for `/users/{userId}/settings/ngp_edits_preview`. */
    ngpEditsPreview(): string {
      return this.settingsDoc('ngp_edits_preview');
    },

    /** Shortcut for `/users/{userId}/settings/stock_edits_preview`. */
    stockEditsPreview(): string {
      return this.settingsDoc('stock_edits_preview');
    },

    /** Path for a user's per-store order settings document. */
    orderSettings(storeId: string): string {
      return `${this.settingsStore(storeId)}/order_settings`;
    },

    /**
     * Set the current user ID for chained path construction.
     *
     * @example
     * const paths = createUserPaths().setUserId('user123');
     * paths.settings(); // → "/users/user123/settings"
     */
    setUserId(user: string): ReturnType<typeof createUserPaths> {
      userId = user;
      return this;
    },

    /** Path for a specific singular document. */
    singularDoc(docName: string): string {
      return `${this.singularDocs()}/${docName}`;
    },

    /** Path for the user's singular documents collection. */
    singularDocs(): string {
      return `${this.base}/${ensureUserId()}/singular_documents`;
    },

    /** Shortcut for `/users/{userId}/settings/table_navigation`. */
    tableNavigation(): string {
      return this.settingsDoc('table_navigation');
    },

    /** Shortcut for `/users/{userId}/settings/fit_to_grid`. */
    fitToGrid(): string {
      return this.settingsDoc('fit_to_grid');
    },

    /** Shortcut for `/users/{userId}/settings/date_formatting`. */
    dateFormatting(): string {
      return `${this.settingsDoc('date_formatting')}`;
    },

    /** Shortcut for `/users/{userId}/settings/store_display_order`. */
    storeDisplayOrder(): string {
      return `${this.settingsDoc('store_display_order')}`;
    },
  };
};

//#endregion
